import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  HStack,
  Box,
  Text,
  Icon,
  IconButton,
  Badge,
  Table,
  Tbody,
  Tr,
  Td,
  useColorMode,
} from "@chakra-ui/react";
import {
  Info,
  FileEdit,
  FileCode,
  Folder as FolderIcon,
  Lock,
  Globe,
  Calendar,
  Clock,
  Eye,
  EyeOff,
  Edit,
  Flame,
  Key,
  Link2,
  Check,
  Copy,
} from "lucide-react";
import { useState } from "react";
import { formatDate, formatBytes } from "@core/utils/format";
import { getShareExtension, buildFullShareUrl, buildRawUrl } from "@/lib/utils/share-url";
import { useCopyToClipboard } from "@/lib/hooks/useCopyToClipboard";
import { detectFormatFromFilename } from "@/lib/utils/filename";
import type { ShareSummary } from "@/lib/api/keys";

interface ShareDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  share: ShareSummary | null;
  getDisplayName: (share: ShareSummary) => string;
  isEncrypted: (share: ShareSummary) => boolean;
  getPasscode: (shareId: string) => string | null;
}

export function ShareDetailsModal({
  isOpen,
  onClose,
  share,
  getDisplayName,
  isEncrypted,
  getPasscode,
}: ShareDetailsModalProps) {
  const { colorMode } = useColorMode();
  const [showPasscode, setShowPasscode] = useState(false);
  const { copy, hasCopied } = useCopyToClipboard();

  const getFullUrl = (s: ShareSummary) => {
    const ext = getShareExtension(s.filename);
    const passcode = getPasscode(s.id);
    return buildFullShareUrl(s.id, ext, passcode);
  };

  const getRawUrlForShare = (s: ShareSummary) => {
    const ext = getShareExtension(s.filename);
    const passcode = getPasscode(s.id);
    return buildRawUrl(s.id, ext, passcode, !isEncrypted(s));
  };

  const handleClose = () => {
    setShowPasscode(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent
        bg={colorMode === "dark" ? "stone.800" : "white"}
        overflow="hidden"
      >
        <ModalHeader display="flex" alignItems="center" gap={2}>
          <Icon as={Info} boxSize={5} />
          Share Details
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflow="hidden">
          {share && (
            <Table
              variant="simple"
              size="sm"
              w="100%"
              sx={{ tableLayout: "fixed" }}
            >
              <Tbody>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                    w="105px"
                    whiteSpace="nowrap"
                  >
                    <HStack spacing={2}>
                      <Icon as={FileEdit} boxSize={4} />
                      <Text>Name</Text>
                    </HStack>
                  </Td>
                  <Td
                    border="none"
                    py={2}
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {getDisplayName(share)}
                  </Td>
                </Tr>
                {share.folderPath &&
                  share.folderPath !== "/" && (
                    <Tr>
                      <Td
                        fontWeight="medium"
                        color="stone.500"
                        border="none"
                        pl={0}
                        py={2}
                      >
                        <HStack spacing={2}>
                          <Icon as={FolderIcon} boxSize={4} />
                          <Text>Folder</Text>
                        </HStack>
                      </Td>
                      <Td border="none" py={2}>
                        <Text fontSize="sm" fontFamily="mono">
                          {share.folderPath}
                        </Text>
                      </Td>
                    </Tr>
                  )}
                {share.tags && (
                  <Tr>
                    <Td
                      fontWeight="medium"
                      color="stone.500"
                      border="none"
                      pl={0}
                      py={2}
                    >
                      <HStack spacing={2}>
                        <Icon as={FileCode} boxSize={4} />
                        <Text>Tags</Text>
                      </HStack>
                    </Td>
                    <Td border="none" py={2}>
                      <HStack spacing={1} flexWrap="wrap">
                        {share.tags.split(",").map((tag) => (
                          <Badge
                            key={tag}
                            size="sm"
                            colorScheme="brand"
                            variant="subtle"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </HStack>
                    </Td>
                  </Tr>
                )}
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon
                        as={isEncrypted(share) ? Lock : Globe}
                        boxSize={4}
                      />
                      <Text>Type</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    <HStack spacing={1}>
                      <Icon
                        as={isEncrypted(share) ? Lock : Globe}
                        boxSize={3}
                        color={
                          isEncrypted(share)
                            ? colorMode === "dark"
                              ? "stone.400"
                              : "stone.500"
                            : colorMode === "dark"
                              ? "brand.400"
                              : "brand.600"
                        }
                      />
                      <Badge
                        colorScheme={
                          isEncrypted(share)
                            ? "gray"
                            : "brand"
                        }
                      >
                        {isEncrypted(share)
                          ? "Protected"
                          : "Public"}
                      </Badge>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon as={FileCode} boxSize={4} />
                      <Text>Format</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    <Badge colorScheme="purple" variant="subtle">
                      {detectFormatFromFilename(
                        share.filename || "",
                      ).toUpperCase()}
                    </Badge>
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon as={Calendar} boxSize={4} />
                      <Text>Created</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    {formatDate(share.createdAt)}
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon as={Clock} boxSize={4} />
                      <Text>Expires</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    {formatDate(share.expiresAt, true)}
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon as={Eye} boxSize={4} />
                      <Text>Views</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    {share.viewCount}
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon as={FileEdit} boxSize={4} />
                      <Text>Size</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    {formatBytes(share.rawSize)}
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                  >
                    <HStack spacing={2}>
                      <Icon as={Edit} boxSize={4} />
                      <Text>Editable</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2}>
                    <Badge
                      colorScheme={
                        share.isModifiable ? "green" : "red"
                      }
                    >
                      {share.isModifiable ? "Yes" : "No"}
                    </Badge>
                  </Td>
                </Tr>
                {share.burnAfterRead && (
                  <Tr>
                    <Td
                      fontWeight="medium"
                      color="stone.500"
                      border="none"
                      pl={0}
                      py={2}
                    >
                      <HStack spacing={2}>
                        <Icon as={Flame} boxSize={4} />
                        <Text>Burn After Read</Text>
                      </HStack>
                    </Td>
                    <Td border="none" py={2}>
                      <Badge colorScheme="orange">Yes</Badge>
                    </Td>
                  </Tr>
                )}
                {isEncrypted(share) &&
                  getPasscode(share.id) && (
                    <Tr>
                      <Td
                        fontWeight="medium"
                        color="stone.500"
                        border="none"
                        pl={0}
                        py={2}
                        verticalAlign="top"
                      >
                        <HStack spacing={2}>
                          <Icon as={Lock} boxSize={4} />
                          <Text>Passcode</Text>
                        </HStack>
                      </Td>
                      <Td border="none" py={2} overflow="hidden">
                        <Box
                          borderRadius="md"
                          px={2}
                          py={1}
                          border="1px"
                          borderColor={
                            colorMode === "dark" ? "stone.700" : "stone.200"
                          }
                          overflow="hidden"
                        >
                          <HStack justify="space-between" spacing={2} minW={0}>
                            <Text
                              fontFamily="mono"
                              fontSize="sm"
                              flex={1}
                              noOfLines={1}
                              minW={0}
                            >
                              {showPasscode
                                ? getPasscode(share.id)
                                : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                            </Text>
                            <IconButton
                              aria-label={
                                showPasscode ? "Hide passcode" : "Show passcode"
                              }
                              icon={
                                <Icon
                                  as={showPasscode ? EyeOff : Eye}
                                  boxSize={3}
                                />
                              }
                              variant="ghost"
                              size="xs"
                              onClick={() => setShowPasscode(!showPasscode)}
                              flexShrink={0}
                            />
                          </HStack>
                        </Box>
                      </Td>
                    </Tr>
                  )}
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                    verticalAlign="top"
                    whiteSpace="nowrap"
                  >
                    <HStack spacing={2}>
                      <Icon as={Key} boxSize={4} />
                      <Text>Share ID</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2} overflow="hidden">
                    <Box
                      borderRadius="md"
                      px={2}
                      py={1}
                      border="1px"
                      borderColor={
                        colorMode === "dark" ? "stone.700" : "stone.200"
                      }
                      overflow="hidden"
                    >
                      <HStack justify="space-between" spacing={2} minW={0}>
                        <Text
                          fontFamily="mono"
                          fontSize="sm"
                          flex={1}
                          noOfLines={1}
                          minW={0}
                        >
                          {share.id}
                        </Text>
                        <IconButton
                          aria-label="Copy share ID"
                          icon={
                            <Icon
                              as={hasCopied('id') ? Check : Copy}
                              boxSize={3}
                            />
                          }
                          variant="ghost"
                          size="xs"
                          colorScheme={hasCopied('id') ? "green" : undefined}
                          onClick={() => copy(share.id, 'id', 'Share ID copied')}
                          flexShrink={0}
                        />
                      </HStack>
                    </Box>
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                    verticalAlign="top"
                    whiteSpace="nowrap"
                  >
                    <HStack spacing={2}>
                      <Icon as={Link2} boxSize={4} />
                      <Text>Share Link</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2} overflow="hidden">
                    <Box
                      borderRadius="md"
                      px={2}
                      py={1}
                      border="1px"
                      borderColor={
                        colorMode === "dark" ? "stone.700" : "stone.200"
                      }
                      overflow="hidden"
                    >
                      <HStack justify="space-between" spacing={2} minW={0}>
                        <Text
                          fontFamily="mono"
                          fontSize="sm"
                          flex={1}
                          noOfLines={1}
                          minW={0}
                        >
                          {getFullUrl(share)}
                        </Text>
                        <IconButton
                          aria-label="Copy link"
                          icon={
                            <Icon
                              as={hasCopied('link') ? Check : Copy}
                              boxSize={3}
                            />
                          }
                          variant="ghost"
                          size="xs"
                          colorScheme={hasCopied('link') ? "green" : undefined}
                          onClick={() => copy(getFullUrl(share), 'link', 'Link copied')}
                          flexShrink={0}
                        />
                      </HStack>
                    </Box>
                  </Td>
                </Tr>
                <Tr>
                  <Td
                    fontWeight="medium"
                    color="stone.500"
                    border="none"
                    pl={0}
                    py={2}
                    verticalAlign="top"
                    whiteSpace="nowrap"
                  >
                    <HStack spacing={2}>
                      <Icon as={FileCode} boxSize={4} />
                      <Text>Raw Link</Text>
                    </HStack>
                  </Td>
                  <Td border="none" py={2} overflow="hidden">
                    <Box
                      borderRadius="md"
                      px={2}
                      py={1}
                      border="1px"
                      borderColor={
                        colorMode === "dark" ? "stone.700" : "stone.200"
                      }
                      overflow="hidden"
                    >
                      <HStack justify="space-between" spacing={2} minW={0}>
                        <Text
                          fontFamily="mono"
                          fontSize="sm"
                          flex={1}
                          noOfLines={1}
                          minW={0}
                        >
                          {getRawUrlForShare(share)}
                        </Text>
                        <IconButton
                          aria-label="Copy raw link"
                          icon={
                            <Icon
                              as={hasCopied('raw') ? Check : Copy}
                              boxSize={3}
                            />
                          }
                          variant="ghost"
                          size="xs"
                          colorScheme={hasCopied('raw') ? "green" : undefined}
                          onClick={() => copy(getRawUrlForShare(share), 'raw', 'Raw link copied')}
                          flexShrink={0}
                        />
                      </HStack>
                    </Box>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
